import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import useFocus from "@/shared/hooks/useFocus";
import { scrollIntoViewIfVisibleLessThan } from "@/shared/utils/dom";

import {
  handleKeyDownNavigation,
  handleKeyDownSelectItem
} from "../../utils/keyboard-navigation/keyboard-navigation";

import styles from "./Accordion.module.scss";

const Accordion = ({
  isCanBeToggled, children, title, className,
  disabled,
  headClassName, isChevronSecondary, bodyClassName,
  isOpenByDefault,
  isOpen: isOpenExternal,
  setIsOpen: setIsOpenExternal,
}) => {
  const refRoot = useRef(null);
  const refContent = useRef(null);

  const [_isOpen, _setIsOpen] = useState(isOpenExternal ?? false);
  const isCanBeToggledFinal = isCanBeToggled && !!children;
  const isOpen = !isCanBeToggledFinal || (isOpenExternal ?? _isOpen);
  const setIsOpen = setIsOpenExternal ?? _setIsOpen;

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpenByDefault !== undefined) {
      setIsOpen(isOpenByDefault);
    }
  }, [isOpenByDefault, setIsOpen]);

  useFocus(isOpen, refContent, {
    isFocusOnFirstIteration: false,
    isScrollFocusedElementIntoView: false,
    onAfterFocus: () => {
      scrollIntoViewIfVisibleLessThan(refContent.current, 0.3);
    },
  });

  const elementChevron = isChevronSecondary ? (
    <span className={classNames({
      "expandItem": isCanBeToggledFinal,
      "expandChevronRotate": isOpen && children,
    })} />
  ) : (
    <span className={classNames({
      "with-chevron": isCanBeToggledFinal,
      "open": isOpen && children,
    })}/>
  );

  const handleKeyDown = (e) => {
    handleKeyDownSelectItem(e, toggleIsOpen)
    || handleKeyDownNavigation(refRoot.current, e);
  };

  return (
    <div
      ref={refRoot}
      className={classNames(styles["root"], "accordion", className, {
        "open": isOpen,
        [styles["toggling-disabled"]]: !isCanBeToggledFinal,
      })}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
    >
      <div
        className={classNames(styles["head"], "accordion_head", headClassName)}
        onClick={isCanBeToggledFinal ? toggleIsOpen : undefined}
      >
        {
          typeof title === "function" ? title(elementChevron) : (
            <div>
              {title}
              {elementChevron}
            </div>
          )
        }
      </div>
      <div ref={refContent} className={classNames(styles["body"], "accordion_body", bodyClassName)}>
        {isOpen && children}
      </div>
    </div>
  );
};

Accordion.defaultProps = {
  isCanBeToggled: true,
};

Accordion.propTypes = {
  children: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  isOpen: PropTypes.bool,
  isOpenByDefault: PropTypes.bool,
  setIsOpen: PropTypes.func,
  isCanBeToggled: PropTypes.bool,
  isChevronSecondary: PropTypes.bool,
  className: PropTypes.string,
  headClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
};

export default Accordion;
